import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Receipt,
  Search,
  Filter,
  Download,
  Plus,
  Euro,
  TrendingUp,
  Clock,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import DashboardLayout from '../components/Layout/DashboardLayout';

const BuchhaltungPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (!token || !userData) {
      navigate('/');
      return;
    }
    
    setUser(JSON.parse(userData));
  }, [navigate]);

  // Mock data for demonstration
  const stats = [
    { label: 'Offene Rechnungen', value: '€ 12.450,00', icon: Clock, color: 'amber' },
    { label: 'Bezahlt (Monat)', value: '€ 28.900,00', icon: CheckCircle, color: 'green' },
    { label: 'Überfällig', value: '€ 3.200,00', icon: AlertCircle, color: 'red' },
    { label: 'Umsatz (Jahr)', value: '€ 156.800,00', icon: TrendingUp, color: 'blue' },
  ];

  const invoices = [
    { id: 'RE-2024-001', kunde: 'Max Mustermann', betrag: 2450.00, status: 'Offen', datum: '15.01.2024' },
    { id: 'RE-2024-002', kunde: 'Erika Schmidt', betrag: 1890.50, status: 'Bezahlt', datum: '12.01.2024' },
    { id: 'RE-2024-003', kunde: 'Thomas Weber', betrag: 3200.00, status: 'Überfällig', datum: '05.01.2024' },
  ];

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <DashboardLayout user={user}>
      <div className="p-4 lg:p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Buchhaltung</h1>
            <p className="text-gray-500 text-sm">Rechnungen und Zahlungen verwalten</p>
          </div>
          <Button className="bg-gradient-to-r from-[#29beff] to-[#5dd3ff] text-white gap-2">
            <Plus className="w-4 h-4" />
            Neue Rechnung
          </Button>
        </div>
        
        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {stats.map((stat, index) => (
            <div key={index} className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-500">{stat.label}</span>
                <stat.icon className={`w-5 h-5 text-${stat.color}-500`} />
              </div>
              <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
            </div>
          ))}
        </div>
        
        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-200">
          {/* Toolbar */}
          <div className="p-4 border-b border-gray-100 flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input placeholder="Rechnungen suchen..." className="pl-10" />
            </div>
            <Button variant="outline" className="gap-2">
              <Filter className="w-4 h-4" />
              Filter
            </Button>
            <Button variant="outline" className="gap-2">
              <Download className="w-4 h-4" />
              Export
            </Button>
          </div>
          
          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <th className="p-4 text-left text-xs font-semibold text-gray-500 uppercase">Rechnungsnr.</th>
                  <th className="p-4 text-left text-xs font-semibold text-gray-500 uppercase">Kunde</th>
                  <th className="p-4 text-left text-xs font-semibold text-gray-500 uppercase">Betrag</th>
                  <th className="p-4 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                  <th className="p-4 text-left text-xs font-semibold text-gray-500 uppercase">Datum</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice) => (
                  <tr key={invoice.id} className="border-b border-gray-100 hover:bg-gray-50/50">
                    <td className="p-4 font-mono text-sm">{invoice.id}</td>
                    <td className="p-4 font-medium text-gray-900">{invoice.kunde}</td>
                    <td className="p-4">€ {invoice.betrag.toLocaleString('de-DE', { minimumFractionDigits: 2 })}</td>
                    <td className="p-4">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                        invoice.status === 'Bezahlt' ? 'bg-green-100 text-green-700' :
                        invoice.status === 'Überfällig' ? 'bg-red-100 text-red-700' :
                        'bg-amber-100 text-amber-700'
                      }`}>
                        {invoice.status}
                      </span>
                    </td>
                    <td className="p-4 text-gray-500">{invoice.datum}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default BuchhaltungPage;
